import React, {
    forwardRef,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import './ActionMenu.scss';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useNavigate } from 'react-router-dom';
import { classNames } from 'primereact/utils';

import LoadingSpinner from '../loadingoverlay/LoadingSpinner';

// @ts-ignore
import { ReactComponent as EditIcon } from '../../assets/images/menu-edit.svg';
// @ts-ignore
import { ReactComponent as DeactivateUserIcon } from '../../assets/images/menu-deactivate.svg';
// @ts-ignore
import { ReactComponent as DeactivateIcon } from '../../assets/images/menu-deactivate2.svg';
// @ts-ignore
import { ReactComponent as ActivateIcon } from '../../assets/images/menu-activate2.svg';
// @ts-ignore
import { ReactComponent as DeleteIcon } from '../../assets/images/menu-delete.svg';
// @ts-ignore
import { ReactComponent as DuplicateIcon } from '../../assets/images/menu-duplicate.svg';
// @ts-ignore
import { ReactComponent as SuccessIcon } from '../../assets/images/success-icon.svg';
// @ts-ignore
import { ReactComponent as TechnicianSwapIcon } from '../../assets/images/technician-swap.svg';
// @ts-ignore
import { ReactComponent as TechnicianAddIcon } from '../../assets/images/technician-add.svg';
// @ts-ignore
import { ReactComponent as CalendarIcon } from '../../assets/images/calendar-icon.svg';
// @ts-ignore
import { ReactComponent as CancelIcon } from '../../assets/images/cancel.svg';
// @ts-ignore
import { ReactComponent as RoutesIcon } from '../../assets/images/map-icon.svg';
// @ts-ignore
import { ReactComponent as AddLocationIcon } from '../../assets/images/add-location-icon.svg';

type ActionMenuIconVariant = 'danger';

type ActionMenuIconConfig = {
    icon: React.ReactElement;
    variant?: ActionMenuIconVariant;
};

type ActionMenuIconKey =
    | 'edit'
    | 'activate'
    | 'deactivateUser'
    | 'deactivate'
    | 'delete'
    | 'duplicate'
    | 'markAsDone'
    | 'changeTechnician'
    | 'appointTechnician'
    | 'rescheduleRoute'
    | 'optimizeRoute'
    | 'cancel'
    | 'addLocation';

interface ActionMenuItem {
    label: string;
    icon?: ActionMenuIconKey | React.ReactElement;
    key: string;
    separatorBefore?: boolean;
    separatorAfter?: boolean;
    disabled?: boolean;
    className?: string;
    variant?: ActionMenuIconVariant;
    navigateTo?: string;
    preCommand?: (
        item: ActionMenuItem,
    ) => boolean | Promise<boolean | void> | void;
    command?: (item: ActionMenuItem) => void;
}

const ActionMenu = forwardRef<any>((_props, ref) => {
    const menuRef = useRef<any>(null);
    const navigate = useNavigate();

    const [items, setItems] = useState<ActionMenuItem[]>([]);
    const [loadingItemKey, setLoadingItemKey] = useState<string | null>(null);

    const icons = useMemo<Record<ActionMenuIconKey, ActionMenuIconConfig>>(
        () => ({
            edit: {
                icon: <EditIcon className="icon" />,
            },
            activate: {
                icon: <ActivateIcon className="icon" />,
            },
            deactivateUser: {
                icon: <DeactivateUserIcon className="icon" />,
            },
            deactivate: {
                icon: <DeactivateIcon className="icon" />,
            },
            delete: {
                icon: <DeleteIcon className="icon" />,
                variant: 'danger',
            },
            duplicate: {
                icon: <DuplicateIcon className="icon" />,
            },
            markAsDone: {
                icon: <SuccessIcon className="icon" />,
            },
            changeTechnician: {
                icon: <TechnicianSwapIcon className="icon" />,
            },
            appointTechnician: {
                icon: <TechnicianAddIcon className="icon" />,
            },
            rescheduleRoute: {
                icon: <CalendarIcon className="icon" />,
            },
            optimizeRoute: {
                icon: <RoutesIcon className="icon" />,
            },
            cancel: {
                icon: <CancelIcon className="icon" />,
                variant: 'danger',
            },
            addLocation: {
                icon: <AddLocationIcon className="icon" />,
            },
        }),
        [],
    );

    const hide = () => {
        menuRef.current?.hide();
        setLoadingItemKey(null);
    };

    const toggle = (event: any, nextItems: ActionMenuItem[]) => {
        menuRef.current?.hide();
        setItems(nextItems);
        setLoadingItemKey(null);
        menuRef.current?.toggle(event);
    };

    useImperativeHandle(ref, () => ({
        toggle,
        hide,
    }));

    return (
        <OverlayPanel ref={menuRef} unstyled>
            <div className="actions-menu">
                <div className="content">
                    {items.map(item => {
                        const isDisabled = item.disabled === true;
                        const isLoading = loadingItemKey === item.key;
                        const isNavigationItem = Boolean(item.navigateTo);

                        const iconConfig =
                            typeof item.icon === 'string'
                                ? icons[item.icon]
                                : undefined;

                        const variant =
                            item.variant ?? iconConfig?.variant;

                        const itemClassName = item.className;

                        const itemClasses = classNames(
                            'item',
                            variant,
                            itemClassName,
                            {
                                disabled: isDisabled || isLoading,
                            },
                        );

                        const handleClick = async (
                            event: React.MouseEvent<HTMLElement>,
                        ) => {
                            if (isDisabled || isLoading) {
                                event.preventDefault();
                                return;
                            }

                            if (item.preCommand) {
                                event.preventDefault();
                                setLoadingItemKey(item.key);

                                try {
                                    const preCommandResult =
                                        await item.preCommand(item);

                                    if (preCommandResult === false) {
                                        setLoadingItemKey(null);
                                        return;
                                    }
                                } catch (error) {
                                    setLoadingItemKey(null);
                                    throw error;
                                }

                                setLoadingItemKey(null);
                            }

                            if (item.command) {
                                event.preventDefault();

                                item.command(item);
                                menuRef.current?.hide();

                                return;
                            }

                            if (
                                item.navigateTo &&
                                !event.ctrlKey &&
                                !event.metaKey &&
                                event.button === 0
                            ) {
                                event.preventDefault();
                                navigate(item.navigateTo);
                            }
                        };

                        const itemContent = (
                            <>
                                <div
                                    className={classNames(
                                        'icon-wrapper',
                                        variant,
                                        itemClassName,
                                    )}
                                >
                                    {isLoading ? (
                                        <LoadingSpinner
                                            size="22px"
                                            color="var(--dark-purple)"
                                        />
                                    ) : (
                                        <>
                                            {iconConfig?.icon}

                                            {React.isValidElement(
                                                item.icon,
                                            ) && item.icon}
                                        </>
                                    )}
                                </div>

                                <div
                                    className={classNames(
                                        'label',
                                        variant,
                                        itemClassName,
                                    )}
                                >
                                    {item.label}
                                </div>
                            </>
                        );

                        return (
                            <React.Fragment key={item.key}>
                                {item.separatorBefore && (
                                    <div className="separator" />
                                )}

                                <div
                                    className={classNames(
                                        'w-full item-wrapper',
                                        variant,
                                        itemClassName,
                                        {
                                            disabled:
                                                isDisabled ||
                                                isLoading,
                                        },
                                    )}
                                >
                                    {isNavigationItem ? (
                                        <a
                                            className={itemClasses}
                                            href={item.navigateTo}
                                            aria-disabled={
                                                isDisabled ||
                                                isLoading
                                            }
                                            tabIndex={
                                                isDisabled ||
                                                isLoading
                                                    ? -1
                                                    : undefined
                                            }
                                            onClick={handleClick}
                                            target="_self"
                                            rel="noopener noreferrer"
                                        >
                                            {itemContent}
                                        </a>
                                    ) : (
                                        <button
                                            type="button"
                                            className={itemClasses}
                                            disabled={
                                                isDisabled ||
                                                isLoading
                                            }
                                            onClick={handleClick}
                                        >
                                            {itemContent}
                                        </button>
                                    )}
                                </div>

                                {item.separatorAfter && (
                                    <div className="separator" />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </OverlayPanel>
    );
});

export default ActionMenu;